/* eslint-disable react/require-default-props */
import React from 'react';
import Debug from 'debug';
import { useTheme } from '@emotion/react';
import { toFormattedDate, toISODate } from './timeAgo';
import CommentDetails, { CommentDetailsProps } from './CommentDetails';
import { ClickableElement, HelpTooltip, ProgressBar } from '@/atoms';
import { LivefireCardType, livefireItemTypes } from '@/constants';
import { EmailLivefireCardType, EventLivefireItemType } from '@/constants/LivefireItemTypes';
import { HelpTooltipLinkDetails, HelpTooltipProps } from '@/atoms/HelpTooltip/HelpTooltip';

// eslint-disable-next-line new-cap
const debug = Debug('livefire:organism:carddetails');

export type CardDetailsProps = {
  recipient: string | null;
  sent: number;
  id: string;
  type: LivefireCardType;
  /** The current value */
  percentWatched?: number;
  maxVideoWatchedPercent?: number;
  onMetricTriggered?: CommentDetailsProps['onMetricTriggered'] | null;
  onTooltipVisible?: HelpTooltipProps['onTooltipVisible'];
  onHelpLinkClick?: (action: HelpTooltipLinkDetails) => void;
  commentMessage?: string | null;
  toggleExpanded: VoidFunction;
  expanded: boolean;
  replyThumbnail?: string | null;
  replyVideoName?: string | null;
  replyVideoUrl?: string | null;
};

/**
 * CardDetails component for recipient/sent info
 */
export function CardDetails({
  recipient,
  sent,
  percentWatched = -1,
  maxVideoWatchedPercent = -1,
  type,
  id,
  onMetricTriggered = () => {},
  onHelpLinkClick = () => {},
  onTooltipVisible = () => {},
  commentMessage = null,
  expanded,
  toggleExpanded,
  replyThumbnail = null,
  replyVideoName = null,
  replyVideoUrl = null,
}: CardDetailsProps) {
  const theme = useTheme();
  const { videoWatched, like, comment, replyWithVideo, EMAIL_TYPES, EVENT_TYPES } =
    livefireItemTypes;

  if (!recipient || !sent || EVENT_TYPES.includes(type as EventLivefireItemType)) {
    return null;
  }

  function normalizeRecipient(recipientName: string | unknown) {
    if (typeof recipientName !== 'string') {
      return recipientName;
    }
    return recipientName.toLowerCase().trim();
  }

  const defaultDescription =
    'You either emailed this video to multiple people or shared it outside of email';
  const isSomeone = normalizeRecipient(recipient) === 'someone';

  const cardRecipient = isSomeone ? (
    <>
      {recipient}
      &nbsp;
      <HelpTooltip
        id={`${id}-help`}
        description={defaultDescription}
        helpLinkUrl="https://support.bombbomb.com/hc/en-us/articles/360042830952-What-does-it-mean-that-someone-viewed-my-video-"
        onTooltipVisible={onTooltipVisible}
        onTooltipLinkClick={(action) => {
          debug('tooltip link clicked', action);
          onHelpLinkClick(action);
        }}
        isRebrand={theme.isRebrand}
      />
    </>
  ) : (
    <span title={recipient}>{recipient}</span>
  );

  const getRecipientLabel = () => {
    let label;
    switch (type) {
      case videoWatched:
        label = 'Viewer:';
        break;
      case replyWithVideo:
        label = 'From:';
        break;
      default:
        label = 'Recipient:';
    }
    return label;
  };

  const renderLikeCommentRecipient = () => (
    <>
      <strong>
        {cardRecipient}
        &nbsp;
      </strong>
      {type === like ? (
        <span>
          <strong className="blue"> liked </strong>
          your video.
        </span>
      ) : (
        <span>
          {}
          <strong className="blue">commented</strong>:
        </span>
      )}
    </>
  );

  const shouldRenderComment =
    (type === comment && commentMessage) || (type === replyWithVideo && commentMessage);
  const shouldRenderProgressBar =
    type === videoWatched && (percentWatched >= 0 || maxVideoWatchedPercent >= 0);
  const isLikeOrComment = type === like || type === comment;

  return (
    <div className="card-details">
      {type === replyWithVideo && replyVideoUrl && replyThumbnail && (
        <div className="reply-with-video">
          <div>
            <ClickableElement
              onClick={() => window.open(replyVideoUrl, '_blank')}
              className="thumbnail-container"
            >
              <img src={replyThumbnail} alt="Video Thumbnail" className="video-thumbnail" />
            </ClickableElement>
          </div>
          <div className="reply-details">
            <p className="recipient" title={`Reply to "${replyVideoName}"`}>
              <strong>Reply to </strong>
              &quot;
              {replyVideoName}
              &quot;
            </p>
            <div className="recipient">
              <strong>{getRecipientLabel()}</strong>
              &nbsp;
              {cardRecipient}
            </div>
          </div>
        </div>
      )}
      {type !== replyWithVideo && (
        <div className={`recipient ${isSomeone ? 'someone' : ''}`}>
          {isLikeOrComment && <>{renderLikeCommentRecipient()}</>}
          {!isLikeOrComment && (
            <>
              <strong>{getRecipientLabel()}</strong>
              &nbsp;
              {cardRecipient}
            </>
          )}
        </div>
      )}
      {EMAIL_TYPES.includes(type as EmailLivefireCardType) && (
        <div className="sent">
          <strong>Sent:</strong>
          &nbsp;
          <abbr title={`${toISODate(sent)}`}>{toFormattedDate(sent)}</abbr>
        </div>
      )}
      {shouldRenderComment && (
        <CommentDetails
          onMetricTriggered={onMetricTriggered}
          expanded={expanded}
          toggleExpanded={toggleExpanded}
          comment={commentMessage}
        />
      )}
      {shouldRenderProgressBar && (
        <div
          className={`percent-watched ${
            percentWatched > maxVideoWatchedPercent ? 'standalone' : ''
          }`}
        >
          <ProgressBar
            className="progress-bar-override"
            size="xsmall"
            textContent="Recent Watch"
            textPlacement="below"
            maxValue={100}
            currentValue={Math.max(percentWatched, 0)}
            highlightValue={90}
            highlightColorName="taosSky"
            font={theme.isRebrand ? 'inter' : 'avenir'}
          />
          {maxVideoWatchedPercent >= percentWatched && (
            <ProgressBar
              className="progress-bar-override"
              size="xsmall"
              textContent="Longest Watch"
              textPlacement="below"
              maxValue={100}
              currentValue={Math.max(maxVideoWatchedPercent, 0)}
              highlightValue={90}
              highlightColorName="taosSky"
              font={theme.isRebrand ? 'inter' : 'avenir'}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default CardDetails;
